import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { Alert } from '../components/Field';
import { money } from '../lib/format';

interface CourseSummary {
  id: string;
  title: string;
  slug: string;
  shortDescription: string;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  thumbnailUrl: string | null;
  category: { name: string };
}

interface TaxLine {
  name: string;
  ratePercent: string;
  amountMinor: number;
}

interface Quote {
  currency: string;
  grossMinor: number;
  discountMinor: number;
  taxableMinor: number;
  taxMinor: number;
  taxBreakup: TaxLine[];
  netMinor: number;
  alreadyOwned: boolean;
  coupon: {
    code: string;
    applied: boolean;
    message: string;
    discountType?: string;
    discountValue?: number;
  } | null;
}

interface PlacedOrder {
  order: { id: string; status: string; netMinor: number };
  payment: { redirectUrl: string | null; status: string } | null;
  settledWithoutPayment: boolean;
  completionPending?: boolean;
}

interface MyOrder {
  id: string;
  status: string;
  netMinor: number;
  currency: string;
}

/**
 * A key per checkout attempt, so a double click or a retried request returns
 * the order already placed instead of charging twice.
 *
 * crypto.getRandomValues rather than randomUUID: the latter only exists on
 * HTTPS pages, and this site may be served over plain HTTP.
 */
function attemptKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function discountLabel(coupon: NonNullable<Quote['coupon']>): string {
  if (coupon.discountType === 'FREE_ACCESS') return '100% off';
  if (coupon.discountType === 'PERCENTAGE') return `${coupon.discountValue}% off`;
  return 'Discount';
}

/**
 * Checkout (promo spec §12, §30).
 *
 * Every amount on this page is what the server returned — the page never adds,
 * subtracts or applies a percentage itself, so what the buyer is shown is
 * exactly what the order will say.
 */
export function Checkout() {
  const { slug } = useParams<{ slug: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const returnedOrder = search.get('order');

  const [course, setCourse] = useState<CourseSummary | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const [codeInput, setCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [promoMessage, setPromoMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [applying, setApplying] = useState(false);

  const [placing, setPlacing] = useState(false);
  const [key, setKey] = useState(attemptKey);
  const [done, setDone] = useState<null | 'enrolled' | 'pending' | 'failed' | 'waiting'>(null);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);

  const priceQuote = useCallback(
    (target: CourseSummary, code: string | null) =>
      api<Quote>('/checkout/quote', {
        method: 'POST',
        body: JSON.stringify({
          items: [{ type: 'COURSE', id: target.id }],
          ...(code ? { couponCode: code } : {}),
        }),
      }),
    [],
  );

  // The course, then a price for it with no code applied.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    api<CourseSummary>(`/courses/${slug}`)
      .then(async (loaded) => {
        if (cancelled) return;
        setCourse(loaded);
        if (!loaded.isFree) setQuote(await priceQuote(loaded, null));
      })
      .catch((caught) => !cancelled && setError(caught as ApiError));
    return () => {
      cancelled = true;
    };
  }, [slug, priceQuote]);

  // Back from the payment page: confirm what actually happened to the order.
  // The gateway's webhook decides, not the redirect, so this may need a moment.
  useEffect(() => {
    if (!returnedOrder) return;
    let cancelled = false;
    let tries = 0;
    setDone('waiting');

    const check = async () => {
      try {
        const { items } = await api<{ items: MyOrder[] }>('/my/orders');
        const order = items.find((candidate) => candidate.id === returnedOrder);
        if (cancelled) return;
        if (order?.status === 'PAID') return setDone('enrolled');
        if (order?.status === 'FAILED' || order?.status === 'CANCELLED') return setDone('failed');
        tries += 1;
        if (tries < 10) window.setTimeout(() => void check(), 1500);
      } catch (caught) {
        if (!cancelled) setError(caught as ApiError);
      }
    };
    void check();
    return () => {
      cancelled = true;
    };
  }, [returnedOrder]);

  async function applyCode(event: FormEvent) {
    event.preventDefault();
    if (!course || !codeInput.trim()) return;
    setApplying(true);
    setPromoMessage(null);
    setError(null);
    try {
      const result = await priceQuote(course, codeInput.trim());
      if (result.coupon?.applied) {
        setQuote(result);
        setAppliedCode(result.coupon.code);
        setPromoMessage({ ok: true, text: result.coupon.message });
        setCodeInput('');
      } else {
        setPromoMessage({
          ok: false,
          text: result.coupon?.message ?? 'This promo code is invalid or has expired.',
        });
      }
      // A different code is a different attempt.
      setKey(attemptKey());
    } catch (caught) {
      const apiError = caught as ApiError;
      setPromoMessage({
        ok: false,
        text:
          apiError.status === 429
            ? 'Too many attempts. Please wait a minute and try again.'
            : apiError.message,
      });
    } finally {
      setApplying(false);
    }
  }

  async function removeCode() {
    if (!course) return;
    setAppliedCode(null);
    setPromoMessage(null);
    setKey(attemptKey());
    setQuote(await priceQuote(course, null));
  }

  async function place() {
    if (!course || !quote) return;
    setPlacing(true);
    setError(null);
    try {
      const result = await api<PlacedOrder>('/checkout', {
        method: 'POST',
        body: JSON.stringify({
          items: [{ type: 'COURSE', id: course.id }],
          ...(appliedCode ? { couponCode: appliedCode } : {}),
          idempotencyKey: key,
          returnUrl: `/checkout/${course.slug}?order={order}`,
        }),
      });

      if (result.settledWithoutPayment) {
        if (result.completionPending) {
          setPendingOrderId(result.order.id);
          setDone('pending');
        } else {
          setDone('enrolled');
        }
        return;
      }
      if (result.payment?.redirectUrl) {
        window.location.assign(result.payment.redirectUrl);
        return;
      }
      // Placed but with nowhere to pay (a manual gateway, or a replayed attempt).
      setSearch({ order: result.order.id });
    } catch (caught) {
      const apiError = caught as ApiError;
      if (apiError.code === 'ALREADY_OWNED') {
        setQuote((current) => (current ? { ...current, alreadyOwned: true } : current));
      } else {
        setError(apiError);
      }
    } finally {
      setPlacing(false);
    }
  }

  async function finishPending() {
    if (!pendingOrderId) return;
    setPlacing(true);
    try {
      await api(`/checkout/orders/${pendingOrderId}/complete`, { method: 'POST' });
      setDone('enrolled');
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setPlacing(false);
    }
  }

  async function goToCourse() {
    if (!course) return;
    try {
      // Enrolling is idempotent; it simply confirms access and opens the course.
      await api('/enrollments', { method: 'POST', body: JSON.stringify({ courseId: course.id }) });
    } catch {
      // Already enrolled, or access is still being confirmed — the player says which.
    }
    navigate(`/learn/${course.id}`);
  }

  // -------------------------------------------------------------- render
  if (error && !course) {
    return <Alert kind="error" title={error.message} />;
  }
  if (!course) {
    return (
      <div className="card">
        <div className="empty">Loading…</div>
      </div>
    );
  }

  if (done === 'enrolled') {
    return (
      <div className="checkout checkout--done">
        <div className="card checkout__result">
          <div className="checkout__seal" aria-hidden="true">
            ✓
          </div>
          <h1 className="page__title">You're enrolled</h1>
          <p className="page__lede">
            <strong>{course.title}</strong> is now in My Learning, ready when you are.
          </p>
          <button className="btn" onClick={() => void goToCourse()}>
            Start learning
          </button>
        </div>
      </div>
    );
  }

  if (done === 'waiting' || done === 'failed' || done === 'pending') {
    return (
      <div className="checkout checkout--done">
        <div className="card checkout__result">
          {done === 'waiting' ? (
            <>
              <h1 className="page__title">Confirming your payment…</h1>
              <p className="page__lede">
                This usually takes a few seconds. You will not be charged twice.
              </p>
              <button className="btn btn--ghost" onClick={() => window.location.reload()}>
                Check again
              </button>
            </>
          ) : done === 'failed' ? (
            <>
              <h1 className="page__title">The payment did not go through</h1>
              <p className="page__lede">Nothing was charged. You can try again.</p>
              <button
                className="btn"
                onClick={() => {
                  setDone(null);
                  setSearch({});
                  setKey(attemptKey());
                }}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <h1 className="page__title">Almost there</h1>
              <p className="page__lede">
                Your order is saved, but we could not finish setting up your access. Your promo code
                is kept for this order.
              </p>
              {error ? <Alert kind="error" title={error.message} /> : null}
              <button className="btn" disabled={placing} onClick={() => void finishPending()}>
                {placing ? 'Finishing…' : 'Finish enrollment'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const free = quote !== null && quote.netMinor === 0 && quote.grossMinor > 0;

  return (
    <div className="checkout">
      <div className="page__head">
        <Link className="muted" to={`/courses/${course.slug}`}>
          ← Back to course
        </Link>
        <h1 className="page__title">Checkout</h1>
      </div>

      <div className="checkout__grid">
        <section className="card checkout__course">
          {course.thumbnailUrl ? (
            <img className="checkout__thumb" src={course.thumbnailUrl} alt="" />
          ) : null}
          <div className="checkout__course-body">
            <p className="checkout__eyebrow">{course.category.name}</p>
            <h2 className="checkout__title">{course.title}</h2>
            <p className="muted">{course.shortDescription}</p>
          </div>
        </section>

        <aside className="card checkout__summary" aria-label="Order summary">
          {course.isFree ? (
            <>
              <p>This course is free — there is nothing to pay.</p>
              <button className="btn btn--block" onClick={() => void goToCourse()}>
                Enrol free
              </button>
            </>
          ) : !quote ? (
            <div className="empty">Pricing…</div>
          ) : quote.alreadyOwned ? (
            <>
              <Alert kind="info" title="You already have access to this course." />
              <button className="btn btn--block" onClick={() => void goToCourse()}>
                Go to course
              </button>
            </>
          ) : (
            <>
              <h2 className="checkout__summary-title">Order summary</h2>

              {appliedCode ? (
                <div className="promo promo--applied">
                  <span>
                    <span aria-hidden="true">✓ </span>
                    <strong className="mono">{appliedCode}</strong> applied
                  </span>
                  <button className="iconbtn" onClick={() => void removeCode()}>
                    Remove
                  </button>
                </div>
              ) : (
                <form className="promo" onSubmit={applyCode}>
                  <label className="promo__label" htmlFor="promo-code">
                    Have a promo code?
                  </label>
                  <div className="promo__row">
                    <input
                      id="promo-code"
                      className="input promo__input"
                      placeholder="Enter promo code"
                      value={codeInput}
                      onChange={(event) => setCodeInput(event.target.value.toUpperCase())}
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={60}
                    />
                    <button
                      className="btn btn--secondary"
                      type="submit"
                      disabled={applying || !codeInput.trim()}
                    >
                      {applying ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                </form>
              )}
              {promoMessage ? (
                <p
                  className={`promo__message promo__message--${promoMessage.ok ? 'ok' : 'bad'}`}
                  role="status"
                >
                  {promoMessage.text}
                </p>
              ) : null}

              <dl className="totals">
                <div>
                  <dt>Original price</dt>
                  <dd>{money(quote.grossMinor, quote.currency)}</dd>
                </div>
                {quote.discountMinor > 0 && quote.coupon ? (
                  <div className="totals__discount">
                    <dt>Discount ({discountLabel(quote.coupon)})</dt>
                    <dd>−{money(quote.discountMinor, quote.currency)}</dd>
                  </div>
                ) : null}
                {quote.taxBreakup.map((line) => (
                  <div key={line.name}>
                    <dt>
                      {line.name} ({Number(line.ratePercent)}%)
                    </dt>
                    <dd>{money(line.amountMinor, quote.currency)}</dd>
                  </div>
                ))}
                <div className="totals__total">
                  <dt>Amount payable</dt>
                  <dd>{money(quote.netMinor, quote.currency)}</dd>
                </div>
              </dl>

              {free ? (
                <p className="checkout__free">
                  <span aria-hidden="true">🎉 </span>Your course is free with this promo code.
                </p>
              ) : null}

              {error ? <Alert kind="error" title={error.message} /> : null}

              <button className="btn btn--block" disabled={placing} onClick={() => void place()}>
                {placing
                  ? free
                    ? 'Enrolling…'
                    : 'Starting payment…'
                  : free
                    ? 'Complete enrollment'
                    : 'Proceed to payment'}
              </button>
              <p className="checkout__fine">
                Prices are confirmed by SkillXzen when you place the order.
              </p>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
